export abstract class ApiBase {
    //basePath = "/startup/api/v1"; - portal
    //basePath = "/api"; - app

    constructor(private basePath: string) {
        this.initAbortOnUnload();
    }

    protected checkError(response: Response): Promise<boolean>{
        return new Promise<boolean>(() => true);
    };

    /**
     * Make a GET request with supplied path and query to portal API.
     * @param pathAndQuery - should only contain the path after v1. ex. /user/search
     * @param bodyData - An optional object or an array with data to include in body as Json
     * @param jsonResponse - when true, returns the result of parsing the response body text as JSON; otherwise the Response object is returned.
     */
    public async get(pathAndQuery: string, jsonResponse: boolean = true, abortKey?: string) {
        const init = this.getRequestInit("GET", null, abortKey);
        return await this.fetch(pathAndQuery, init, jsonResponse);
    }

    /**
     * Make a POST request with supplied path and query to portal API.
     * @param pathAndQuery - should only contain the path after v1. ex. /user/search
     * @param bodyData - An optional object or an array with data to include in body as Json
     * @param jsonResponse - when true, returns the result of parsing the response body text as JSON; otherwise the Response object is returned.
     */
    public async post(pathAndQuery: string, bodyData?: any, jsonResponse: boolean = true, abortKey?: string) {
        const init = this.getRequestInit("POST", bodyData, abortKey);
        return await this.fetch(pathAndQuery, init, jsonResponse);
    }

    /**
     * Make a PUT request with supplied path and query to portal API.
     * @param pathAndQuery - should only contain the path after v1. ex. /user/search
     * @param bodyData - An optional object or an array with data to include in body as Json
     * @param jsonResponse - when true, returns the result of parsing the response body text as JSON; otherwise the Response object is returned.
     */
    public async put(pathAndQuery: string, bodyData?: any, jsonResponse: boolean = true, abortKey?: string) {
        const init = this.getRequestInit("PUT", bodyData, abortKey);
        return await this.fetch(pathAndQuery, init, jsonResponse);
    }
    
    /**
     * Make a DELETE request with supplied path and query to portal API.
     * @param pathAndQuery - should only contain the path after v1. ex. /user/search
     * @param bodyData - An optional object or an array with data to include in body as Json
     * @param jsonResponse - when true, returns the result of parsing the response body text as JSON; otherwise the Response object is returned.
     */
    public async delete(pathAndQuery: string, bodyData?: any, jsonResponse: boolean = true, abortKey?: string) {
        const init = this.getRequestInit("DELETE", bodyData, abortKey);
        return await this.fetch(pathAndQuery, init, jsonResponse);
    }

    /**
     * Aborts a request by the key used to identify it
     * @param abortKey - The key used to identify the request to abort
     */
    public abortRequestByKey(abortKey: string) {
        if (this.abortControllers[abortKey]) {
            this.abortControllers[abortKey].abort();
            delete this.abortControllers[abortKey];
        }
    }

    private defaultAbortController = new AbortController();
    private abortControllers: { [key: string]: AbortController } = {};

    public async fetch(pathAndQuery: string, requestInit: RequestInit, jsonResponse: boolean) : Promise<any> {
        let response: Response;

        try {
            response = await fetch(`${this.basePath}${pathAndQuery}`, requestInit);
        }
        catch (error: any) {
            if (error.name !== 'AbortError') {
                throw error;
            }

            return null;
        }

        if (!await this.checkError(response)) {
            return null;
        }

        if (jsonResponse)
        {
            return await response.json();
        }

        return response;
    }

    private getRequestInit(method: string, bodyData?: any, abortKey?: string) {
        const body = bodyData ? JSON.stringify(bodyData) : null;
        
        if (abortKey?.length && !this.abortControllers[abortKey]) {
            this.abortControllers[abortKey] = new AbortController();
        }

        const abortController = abortKey?.length ? this.abortControllers[abortKey] : this.defaultAbortController;

        return {
            method: method,
            signal: abortController.signal,
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            body: body
        } as RequestInit;
    }

    private initAbortOnUnload() {
        window.addEventListener("beforeunload", () => {
            Object.keys(this.abortControllers).forEach(key => {
                this.abortControllers[key].abort();
            });
            this.defaultAbortController.abort();
        });
    }
}